/**
 * Detect Safe Exam Browser JS API. User-Agent is informational only —
 * never use it for access control (server Config Key is authoritative).
 *
 * Prefer Modern WebView may expose SafeExamBrowser before configKey is
 * populated; we retry updateKeys until a non-empty configKey appears.
 */
import { useEffect, useState } from 'react';

const KEY_WAIT_MS = 12000;
const KEY_POLL_MS = 250;

function readSebKeys() {
  const seb = typeof window !== 'undefined' ? window.SafeExamBrowser : undefined;
  const ck = seb?.security?.configKey;
  if (!ck) return null;
  return {
    configKey: ck,
    browserExamKey: seb.security.browserExamKey || undefined,
  };
}

export function useSebEnvironment() {
  const [sebState, setSebState] = useState({
    checked: false,
    jsApiAvailable: false,
    uaMentionsSeb: false,
    keys: null,
  });

  useEffect(() => {
    const ua = navigator.userAgent || '';
    const uaMentionsSeb = /SEB[/ ]/i.test(ua);
    let cancelled = false;
    let timer = null;

    const finish = (jsApiAvailable, keys) => {
      if (cancelled) return;
      setSebState({ checked: true, jsApiAvailable, uaMentionsSeb, keys });
    };

    const seb = typeof window !== 'undefined' ? window.SafeExamBrowser : undefined;
    if (!seb?.security) {
      finish(false, null);
      return () => { cancelled = true; };
    }

    const tryFinishWithKeys = () => {
      const keys = readSebKeys();
      if (keys?.configKey) {
        finish(true, keys);
        return true;
      }
      return false;
    };

    if (tryFinishWithKeys()) {
      return () => { cancelled = true; };
    }

    const started = Date.now();
    const poll = () => {
      if (cancelled) return;
      if (tryFinishWithKeys()) return;
      if (typeof seb.security.updateKeys === 'function') {
        try {
          seb.security.updateKeys(() => {
            if (cancelled) return;
            if (tryFinishWithKeys()) return;
            if (Date.now() - started < KEY_WAIT_MS) {
              timer = setTimeout(poll, KEY_POLL_MS);
            } else {
              // JS API present but keys never arrived — still mark checked so
              // SebGate can show a clear missing_config_key path.
              finish(true, null);
            }
          });
          return;
        } catch {
          /* fall through to timed poll */
        }
      }
      if (Date.now() - started < KEY_WAIT_MS) {
        timer = setTimeout(poll, KEY_POLL_MS);
      } else {
        finish(true, null);
      }
    };

    poll();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  return sebState;
}
